import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchUserPostsActivity } from "@/lib/community";
interface IProps {
  userId: string;
}

function getDateKey(date: Date) {
  return date.toISOString().slice(0, 10);
}

function getMondayBasedDay(date: Date) {
  return (date.getDay() + 6) % 7;
}

const getIntensityColor = (intensity: number) => {
  switch (intensity) {
    case 0:
      return "bg-gray-100";
    case 1:
      return "bg-green-200";
    case 2:
      return "bg-green-400";
    case 3:
      return "bg-green-600";
    case 4:
      return "bg-green-800";
    default:
      return "bg-gray-100";
  }
};

const GitHubHeatmap = ({ userId }: IProps) => {
  const { data: posts, isLoading } = useQuery({
    queryKey: ["posts-activity", userId],
    queryFn: () => fetchUserPostsActivity({ userId }),
    staleTime: 1000 * 60 * 5,
  });

  const year = new Date().getFullYear();

  const activityMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (posts) {
      posts.forEach((post) => {
        const key = post.created_at.slice(0, 10);
        map[key] = (map[key] ?? 0) + 1;
      });
    }
    return map;
  }, [posts]);

  const yearData = useMemo(() => {
    const weeks: { intensity: number; date: Date }[][] = [];
    const start = new Date(year, 0, 1);
    const dayOfWeek = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - dayOfWeek);
    const current = new Date(start);

    for (let week = 0; week < 53; week++) {
      const weekData = [];
      for (let day = 0; day < 7; day++) {
        const key = getDateKey(current);
        const raw = activityMap[key] ?? 0;
        let intensity = 0;
        if (raw > 0) intensity = Math.min(4, raw);
        weekData.push({ intensity, date: new Date(current) });
        current.setDate(current.getDate() + 1);
      }
      weeks.push(weekData);
    }
    return weeks;
  }, [activityMap, year]);

  const getMonthPositions = () => {
    const positions = [];
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    let month = 0;
    for (let w = 0; w < yearData.length; w++) {
      for (let d = 0; d < 7; d++) {
        const cell = yearData[w][d];
        if (cell.date.getDate() === 1 && cell.date.getMonth() === month) {
          positions.push({
            month: monthNames[month],
            position: w * 14,
          });
          month++;
        }
      }
      if (month >= 12) break;
    }
    return positions;
  };

  const monthPositions = getMonthPositions();

  return (
    <div className="bg-background rounded-lg text-foreground">
      <div className="overflow-x-auto">
        <div className="min-w-max">
          <div className="relative h-4 mb-2" style={{ marginLeft: "44px" }}>
            {monthPositions.map((pos, index) => (
              <span
                key={index}
                className="absolute text-xs text-gray-500"
                style={{ left: `${pos.position}px` }}
              >
                {pos.month}
              </span>
            ))}
          </div>
          <div className="flex">
            <div className="flex flex-col text-xs text-gray-500 w-11 mr-1">
              <div className="h-3 mb-0.5 leading-3"></div>
              <div className="h-3 mb-0.5 leading-3">Mon</div>
              <div className="h-3 mb-0.5 leading-3"></div>
              <div className="h-3 mb-0.5 leading-3">Wed</div>
              <div className="h-3 mb-0.5 leading-3"></div>
              <div className="h-3 mb-0.5 leading-3">Fri</div>
              <div className="h-3 mb-0.5 leading-3"></div>
            </div>
            <div className="flex gap-0.5">
              {isLoading
                ? Array.from({ length: 53 }).map((_, weekIndex) => (
                    <div key={weekIndex} className="flex flex-col gap-0.5">
                      {Array.from({ length: 7 }).map((_, dayIndex) => (
                        <div
                          key={`${weekIndex}-${dayIndex}`}
                          className="w-[0.7rem] h-[0.7rem] rounded-sm bg-gray-200 animate-pulse"
                        />
                      ))}
                    </div>
                  ))
                : yearData.map((week, weekIndex) => (
                    <div key={weekIndex} className="flex flex-col gap-0.5">
                      {week.map((day, dayIndex) => (
                        <div
                          key={`${weekIndex}-${dayIndex}`}
                          className={`w-[0.7rem] h-[0.7rem] rounded-sm ${getIntensityColor(
                            day.intensity
                          )}`}
                          title={`${day.intensity} posts\n${day.date
                            .toISOString()
                            .slice(0, 10)}`}
                        />
                      ))}
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center text-xs text-gray-500">
          <span className="mr-2">Less</span>
          <div className="flex gap-1">
            <div className="w-3 h-3 bg-gray-100 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-200 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-400 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-600 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-800 rounded-sm"></div>
          </div>
          <span className="ml-2">More</span>
        </div>
      </div>
    </div>
  );
};

export default GitHubHeatmap;
